/**
 * Audit Logger Utility Functions
 *
 * Helper functions for audit logging operations
 */

import { PermissionActionType, AuditEvent, AuditLevel } from './types';
import { SecurityEvent } from '../../permission-integrity';

/**
 * Generate unique session identifier
 */
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate unique event identifier
 */
export function generateEventId(): string {
  return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Hash user ID for anonymization
 */
export function hashUserId(userId?: string): string | undefined {
  if (!userId) return undefined;
  // Simple hash for anonymization - in production use proper crypto
  return `user_${userId.split('').reduce((hash, char) =>
    ((hash << 5) - hash) + char.charCodeAt(0), 0
  ).toString(36)}`;
}

/**
 * Hash resource ID for anonymization
 */
export function hashResourceId(resourceId?: string): string | undefined {
  if (!resourceId) return undefined;
  return `resource_${resourceId.split('').reduce((hash, char) =>
    ((hash << 5) - hash) + char.charCodeAt(0), 0
  ).toString(36)}`;
}

/**
 * Get user agent string safely
 */
export function getUserAgent(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
}

/**
 * Get client IP address (placeholder for server-side implementation)
 */
export function getClientIP(): string {
  // In a real implementation, this would come from the server
  return 'client-side-unknown';
}

/**
 * Map security event severity to audit level
 */
export function mapSecurityLevel(severity: SecurityEvent['severity']): AuditLevel {
  const mapping = {
    'LOW': 'INFO' as const,
    'MEDIUM': 'WARN' as const,
    'HIGH': 'ERROR' as const,
    'CRITICAL': 'CRITICAL' as const
  };
  return mapping[severity];
}

/**
 * Generate compliance flags based on permission and result
 */
export function generateComplianceFlags(permission?: string, result?: string): string[] {
  const flags: string[] = [];

  if (result === 'DENY') {
    flags.push('ACCESS_DENIED');
  }

  if (permission?.includes('admin') || permission?.includes('system')) {
    flags.push('PRIVILEGED_ACCESS');
  }

  if (permission?.includes('delete') || permission?.includes('destroy')) {
    flags.push('DESTRUCTIVE_ACTION');
  }

  return flags;
}

// Environment levels for configuration
export type EnvironmentLevel = 'DEVELOPMENT' | 'STAGING' | 'PRODUCTION' | 'ENTERPRISE';

/**
 * Determine if an event should be logged based on environment level
 */
export function shouldLog(environmentLevel: EnvironmentLevel, _action: PermissionActionType, result: string): boolean {
  switch (environmentLevel) {
    case 'DEVELOPMENT':
      return result === 'DENY' || result === 'ERROR';
    case 'STAGING':
      return true;
    case 'PRODUCTION':
      return true;
    case 'ENTERPRISE':
      return true;
    default:
      return result === 'DENY' || result === 'ERROR';
  }
}

/**
 * Configure environment-specific settings
 */
export function configureForEnvironment(environmentLevel: EnvironmentLevel): {
  maxEvents: number;
  retentionDays: number;
} {
  switch (environmentLevel) {
    case 'DEVELOPMENT':
      return { maxEvents: 1000, retentionDays: 1 };
    case 'STAGING':
      return { maxEvents: 5000, retentionDays: 7 };
    case 'PRODUCTION':
      return { maxEvents: 10000, retentionDays: 30 };
    case 'ENTERPRISE':
      return { maxEvents: 50000, retentionDays: 90 };
    default:
      return { maxEvents: 5000, retentionDays: 7 };
  }
}

/**
 * Detect environment level from environment
 */
export function getEnvironmentLevel(): EnvironmentLevel {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.includes('dev')) return 'DEVELOPMENT';
    if (hostname.includes('staging')) return 'STAGING';
    if (hostname.includes('enterprise')) return 'ENTERPRISE';
  }
  return 'PRODUCTION';
}

/**
 * Create audit event with common fields
 */
export function createAuditEvent(
  sessionId: string,
  params: Partial<AuditEvent> & {
    action: PermissionActionType;
    level: AuditLevel;
    result: 'SUCCESS' | 'FAILURE' | 'DENIED';
    details: Record<string, unknown>;
    environment: 'development' | 'staging' | 'production';
  }
): AuditEvent {
  return {
    id: generateEventId(),
    timestamp: new Date().toISOString(),
    sessionId,
    userAgent: getUserAgent(),
    ipAddress: getClientIP(),
    metadata: {},
    ...params
  };
}